/**
 * governance/utils/severity.ts
 * Canonical severity types, ordering, helpers, and display utilities.
 *
 * ─── Phase 1: Severity 3→5 model ──────────────────────────────────────────────
 * SeverityLevel is the DISPLAY taxonomy used by the admin UI. It is a
 * display-only refinement of `ValidationIssue.kind` ('blocker'|'coach'|'info')
 * combined with the numeric `ValidationIssue.severity` (0-100). It does NOT
 * change verdict logic in any way — the pipeline gate still computes
 * FAIL/REVIEW_NEEDED/PASS purely from kind + numeric severity.
 *
 * 5-level model:
 *   - 'critical' → blocker + severity ≥ 90 (student-facing risk, e.g. answer leak)
 *   - 'blocker'  → blocker + severity 70-89 (pedagogically unsound)
 *   - 'review'   → coach   + severity ≥ reviewCoachSeverityThreshold (45) — uncertainty
 *   - 'coach'    → coach   + severity < 45 (author quality)
 *   - 'info'     → info    (observation only)
 *
 * Use `deriveDisplaySeverity(kind, severity)` to map from a ValidationIssue.
 */

import type { IssueKind } from '../pipeline/issues/types'

/** Canonical display severity — 5 levels (Phase 1) */
export type SeverityLevel = 'critical' | 'blocker' | 'review' | 'coach' | 'info'

export const SEVERITY_ORDER: Record<SeverityLevel, number> = {
  critical: 0,
  blocker: 1,
  review: 2,
  coach: 3,
  info: 4,
}

export function compareSeverity(a: SeverityLevel, b: SeverityLevel): number {
  return SEVERITY_ORDER[a] - SEVERITY_ORDER[b]
}

/** A severity is "blocking" (gate FAIL) if it is critical or blocker. */
export function isBlockingSeverity(level: SeverityLevel): boolean {
  return level === 'critical' || level === 'blocker'
}

export function severityLabel(level: SeverityLevel): string {
  const labels: Record<SeverityLevel, string> = {
    critical: '🔴 Kritiek',
    blocker: '🟠 Blocker',
    review: '🟡 Review',
    coach: '🔵 Coach',
    info: '⚪ Info',
  }
  return labels[level]
}

export function severityColor(level: SeverityLevel): string {
  const colors: Record<SeverityLevel, string> = {
    critical: '#dc2626', // red-600
    blocker: '#ea580c', // orange-600
    review: '#ca8a04', // yellow-600
    coach: '#2563eb', // blue-600
    info: '#64748b', // slate-500
  }
  return colors[level]
}

/**
 * Threshold at/above which a COACH issue is treated as 'review' (uncertainty).
 *
 * ─── SSOT (detangle §10.5, consolidated juni 2026) ───────────────────────────
 * This is the single numeric source for the review-coach threshold. It lives
 * here (a leaf-ish module that imports only the `IssueKind` type) so both the
 * gate-verdict logic and the display taxonomy read the SAME number without a
 * circular import — symmetric with `CRITICAL_BLOCKER_SEVERITY_THRESHOLD` below.
 *
 * Consumers that previously inlined `45` and now import this const:
 *   - gate/evaluate.ts → DEFAULT_GATE_THRESHOLDS.reviewCoachSeverityThreshold
 *   - defaultPolicy.ts → GATES.reviewCoachSeverityThreshold
 *   - usePipelineRun.ts (admin) → GATE_DEFAULTS.reviewCoachSeverityThreshold (via barrel)
 *   - deriveDisplaySeverity() below (the display layer)
 *
 * Changing this value shifts when a coach issue becomes REVIEW_NEEDED in the
 * gate AND when it renders as 'review' in the admin — by design they must move
 * together. Runtime gate-defaults can still be overridden per-policy at runtime;
 * this is the fallback/default the whole stack agrees on.
 */
export const REVIEW_COACH_SEVERITY_THRESHOLD = 45

/**
 * Threshold above which a blocker issue is displayed as 'critical'
 * (student-facing risk — e.g. answer leak, indicator parity).
 *
 * Exported so `gate/evaluate.ts` (the SSOT for verdict logic) can import
 * the same constant for its [KRITIEK] reason-string prefix — eliminating
 * the previous magic-number duplication.
 */
export const CRITICAL_BLOCKER_SEVERITY_THRESHOLD = 90

/**
 * Derive the display severity for a ValidationIssue from its kind and
 * numeric severity.
 *
 * This is the canonical mapping point for the 5-level display model.
 * The pipeline still emits issues with kind='blocker'|'coach'|'info'; this
 * helper produces the richer 5-level label for UI display only.
 *
 * @param kind     The ValidationIssue.kind ('blocker' | 'coach' | 'info')
 * @param severity The numeric ValidationIssue.severity (0-100)
 * @returns        The display-severity bucket
 */
export function deriveDisplaySeverity(
  kind: IssueKind,
  severity: number,
): SeverityLevel {
  if (kind === 'blocker') {
    return severity >= CRITICAL_BLOCKER_SEVERITY_THRESHOLD
      ? 'critical'
      : 'blocker'
  }
  if (kind === 'coach') {
    return severity >= REVIEW_COACH_SEVERITY_THRESHOLD ? 'review' : 'coach'
  }
  return 'info'
}
